import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaCheckCircle,
  FaCompass,
  FaSearch,
  FaSpinner,
  FaStar,
  FaUserFriends,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/apiService';
import SocialNav from '../../components/SocialNav';
import '../UserProfile/UserProfile.css';
import '../UserProfile/SocialPremium.css';

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

export default function Explore() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await userService.getSuggestedProfiles(8);
        if (active) setSuggested(res.data.profiles || []);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setBootLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 1) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const res = await userService.searchUsers(q, 20);
        if (active) setResults(res.data.users || []);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }, 280);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  const list = query.trim() ? results : suggested;

  return (
    <main className="social-page explore-page">
      <div className="social-orb social-orb-a" aria-hidden="true" />
      <div className="social-orb social-orb-b" aria-hidden="true" />
      <SocialNav username={user?.username} />

      <div className="social-shell explore-layout">
        <section className="explore-hero">
          <div className="explore-hero-grid" aria-hidden="true" />
          <div className="explore-hero-content">
            <span className="social-page-kicker"><FaCompass /> {t('explore.kicker')}</span>
            <h1>{t('explore.titleBefore')}<br /><em>{t('explore.titleEm')}</em></h1>
            <p>{t('explore.subtitle')}</p>

            <div className="explore-search premium-search">
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value.slice(0, 40))}
                placeholder={t('explore.searchPlaceholder')}
                autoComplete="off"
                aria-label={t('explore.searchAria')}
              />
              <span className="explore-search-hint">ENTER</span>
            </div>
          </div>
          <div className="explore-hero-art" aria-hidden="true">
            <span className="explore-orbit orbit-one"><FaStar /></span>
            <span className="explore-orbit orbit-two"><FaUserFriends /></span>
            <span className="explore-art-core"><FaCompass /></span>
          </div>
        </section>

        {error && <div className="social-alert error">{error}</div>}

        <section className="explore-community-section">
          <div className="explore-section-head">
            <div>
              <span className="social-card-eyebrow">
                {query.trim() ? t('explore.searchEyebrow') : t('explore.selectionEyebrow')}
              </span>
              <h2>{query.trim() ? t('explore.resultsFor', { query: query.trim() }) : t('explore.peopleToMeet')}</h2>
              <p>
                {query.trim()
                  ? t('explore.resultsHint')
                  : t('explore.selectionHint')}
              </p>
            </div>
            {!query.trim() && (
              <span className="explore-count-badge">
                <strong>{suggested.length}</strong> {t('explore.featuredLabel')}
              </span>
            )}
          </div>

          {(bootLoading || loading) && (
            <div className="explore-loading-grid">
              <span /><span /><span /><span />
              <FaSpinner className="social-spin" />
            </div>
          )}

          {!bootLoading && !loading && list.length === 0 && (
            <div className="social-empty-state explore-empty">
              <span className="social-empty-icon"><FaSearch /></span>
              <strong>{query.trim() ? t('explore.noResultsTitle') : t('explore.growingTitle')}</strong>
              <p>
                {query.trim()
                  ? t('explore.noResultsText')
                  : t('explore.growingText')}
              </p>
            </div>
          )}

          {!bootLoading && !loading && list.length > 0 && (
            <div className="explore-results">
              <ul className="explore-user-grid">
                {list.map((person) => {
                  const name = person.profile?.displayName || person.username;
                  return (
                    <li key={person.id} className={`explore-user-card${person.systemAccount?.featured ? ' is-system-featured' : ''}`}>
                      <div className="explore-user-cover" aria-hidden="true">
                        <span />
                      </div>
                      <Link to={`/perfil/${person.username}`} className="explore-user-main">
                        {person.systemAccount?.enabled && (
                          <span className="explore-system-label">{t('common.officialAccount')}</span>
                        )}
                        <span className="social-avatar explore-avatar" aria-hidden="true">
                          {person.avatarUrl ? <img src={person.avatarUrl} alt="" /> : getInitials(name)}
                        </span>
                        <div className="explore-user-name">
                          <strong>
                            {name}
                            {person.verified && <FaCheckCircle title={t('common.verifiedProfile')} />}
                          </strong>
                          <small>@{person.username}</small>
                        </div>
                        <p>{person.profile?.bio || t('common.defaultBio')}</p>
                        {(person.badges || []).length > 0 && (
                          <div className="explore-user-badges">
                            {person.badges.slice(0, 2).map((badge) => (
                              <span key={badge.id || badge.slug}>{badge.name}</span>
                            ))}
                          </div>
                        )}
                        <span className="explore-profile-cta">
                          {t('explore.meetProfile')} <FaArrowRight />
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
